import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2 } from "lucide-react";
import { adminApi, formatApiError } from "@/lib/adminApi";
import { BrandBand } from "@/components/vendor/Brand";
import { PasswordInput } from "@/components/admin/PasswordInput";
import "@/styles/hausa-vendor.css";

const PwField = ({ id, label, value, onChange, testId }) => (
  <label className="hx-field" htmlFor={id} style={{ marginTop: 16 }}>
    <span className="hx-label">{label}</span>
    <PasswordInput id={id} value={value} onChange={onChange} testId={testId} autoComplete="new-password" minLength={8} maxLength={128} />
  </label>
);

export default function AdminReset() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [status, setStatus] = useState("idle");

  const submit = async (e) => {
    e.preventDefault();
    if (pw.trim().length < 8) return setError("Password must be at least 8 characters.");
    if (pw !== confirm) return setError("Passwords do not match.");
    setError("");
    setStatus("saving");
    try {
      await adminApi.post("/admin/reset-password", { token, new_password: pw });
      setStatus("done");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail, "Could not reset the password."));
      setStatus("idle");
    }
  };

  return (
    <div className="hx" data-testid="admin-reset-page">
      <BrandBand />
      <div className="hx-container hx-login hx-fade">
        {status === "done" ? (
          <div data-testid="admin-reset-success">
            <CheckCircle2 size={32} strokeWidth={1.5} color="#5A776F" />
            <h1 style={{ marginTop: 12 }}>Password updated</h1>
            <p className="hx-muted" style={{ marginBottom: 24 }}>You can now sign in with your new password.</p>
            <Link to="/admin" className="hx-btn" style={{ width: "100%" }} data-testid="admin-reset-go-login">Go to sign in</Link>
          </div>
        ) : !token ? (
          <div data-testid="admin-reset-missing-token">
            <h1>Reset link missing</h1>
            <p className="hx-muted">Open the link from the reset email, or request a new one from the sign-in page.</p>
            <Link to="/admin" className="hx-link" style={{ display: "inline-block", marginTop: 16 }}>Back to sign in</Link>
          </div>
        ) : (
          <form onSubmit={submit} noValidate>
            <h1>Choose a new password</h1>
            <p className="hx-muted">At least 8 characters.</p>
            <PwField id="new-password" label="New password" value={pw} onChange={setPw} testId="admin-reset-password-input" />
            <PwField id="confirm-password" label="Confirm password" value={confirm} onChange={setConfirm} testId="admin-reset-confirm-input" />
            {error && <p className="hx-alert" style={{ marginTop: 14 }} data-testid="admin-reset-error">{error}</p>}
            <button className="hx-btn" style={{ marginTop: 20, width: "100%" }} disabled={status === "saving" || !pw || !confirm} data-testid="admin-reset-submit-button">
              {status === "saving" ? "Saving…" : "Save new password"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
